import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import * as yup from 'yup';
import { IBranchViewModel } from 'viewModels';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTheme } from 'styled-components';
import { MASK_CNPJ } from '../../../constants';
import { Button } from '../../Buttons/Button';
import { Input } from '../../Inputs/Input';
import { Form, Header } from '../ModalEditBranch/style';
import { useAlert } from '../../../hooks/useAlert';
import { cnpj } from 'cpf-cnpj-validator';
import { unMask } from '../../../utils/removeMask';

interface ModalCreateBranchProps {
  isOpen: boolean;
  runningTutorial: boolean;
  onRequestClose: () => void;
  onRequestCreateBranch: (branch: IBranchViewModel) => void;
  companyId?: string;
  isRequesting?: boolean;
}

const branchSchema = yup.object().shape({
  cnpj: yup
    .string()
    .required('Campo obrigatório')
    .test('cnpjValid', 'CNPJ inválido', (value: string) => {
      if (!value) return false;
      return cnpj.isValid(unMask(value));
    }),
  name: yup.string().required('Campo obrigatório'),
  postalCode: yup.string().required('Campo obrigatório').length(8, 'Deve ter 8 dígitos'),
  street: yup.string().required('Campo obrigatório'),
  number: yup.number().typeError('Apenas números são permitidos').required('Campo obrigatório'),
  complement: yup.string().nullable(),
  city: yup.string().required('Campo obrigatório'),
  uf: yup.string().required('Campo obrigatório').length(2, 'Deve ter 2 caracteres'),
});

export function ModalCreateBranch({
  isOpen,
  onRequestClose,
  onRequestCreateBranch,
  runningTutorial,
  companyId,
  isRequesting = false,
}: ModalCreateBranchProps) {
  const alert = useAlert();
  const theme = useTheme();

  const {
    handleSubmit,
    formState: { errors },
    getValues,
    control,
    reset,
  } = useForm({
    reValidateMode: 'onChange',
    defaultValues: {
      cnpj: '',
      name: '',
      postalCode: '',
      street: '',
      number: '',
      complement: '',
      city: '',
      uf: '',
    },
    resolver: yupResolver(branchSchema),
  });

  const handleCreateBranch = () => {
    const newBranch: IBranchViewModel = {
      id: '',
      name: getValues('name'),
      companyId: companyId || '',
      cnpj: unMask(getValues('cnpj')),
      address: {
        latitude: 0,
        longitude: 0,
        city: getValues('city'),
        cityCode: 0,
        uf: getValues('uf'),
        ufCode: 0,
        number: Number(getValues('number')),
        complement: getValues('complement') || undefined,
        postalCode: getValues('postalCode'),
        street: getValues('street'),
      },
    };
    onRequestCreateBranch(newBranch);
    alert.show({ message: 'Filial cadastrada com sucesso!', type: 'success' });
    reset();
    onRequestClose();
  };

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={isRequesting ? () => {} : onRequestClose}
      overlayClassName="react-modal-overlay"
      className="react-edit-modal-content">
      <Header>
        <div className="subHeader">
          <AddIcon 
            style={{ 
              fontSize: '2rem', 
              color: theme.colors.secondary,
              width: '2rem',
              height: '2rem'
            }} 
          />
          <h1>Criar filial</h1>
        </div>
        <div className="close-div">
          <button type="button" onClick={onRequestClose} disabled={isRequesting}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </Header>
      <Form>
        <div className="containerName">
          <Input
            name="cnpj"
            variant="standard"
            control={control}
            label="CNPJ"
            lengthInput={18}
            mask={MASK_CNPJ}
            preventNotNumbers
            errorText={errors.cnpj?.message}
            disabled={isRequesting}
          />
        </div>
        <div className="containerName">
          <Input
            name="name"
            variant="standard"
            control={control}
            label="Nome"
            lengthInput={50}
            errorText={errors.name?.message}
            disabled={isRequesting}
          />
        </div>
        <div className="containerAddress">
          <Input
            name="postalCode"
            variant="standard"
            control={control}
            label="CEP"
            className="postalCodeInput"
            lengthInput={8}
            errorText={errors.postalCode?.message}
            preventNotNumbers
            disabled={isRequesting}
          />
          <Input
            name="street"
            variant="standard"
            control={control}
            label="Endereço"
            lengthInput={50}
            className="street"
            errorText={errors.street?.message}
            disabled={isRequesting}
          />
          <Input
            name="number"
            variant="standard"
            control={control}
            label="Número"
            lengthInput={5}
            className="number"
            errorText={errors.number?.message}
            preventNotNumbers
            disabled={isRequesting}
          />
        </div>
        <div className="containerComplement">
          <Input
            name="complement"
            variant="standard"
            control={control}
            lengthInput={50}
            label="Complemento"
            className="complement"
            errorText={errors.complement?.message}
            disabled={isRequesting}
          />
        </div>
        <div className="containerAddress">
          <Input
            name="city"
            variant="standard"
            control={control}
            label="Cidade"
            lengthInput={50}
            className="street"
            errorText={errors.city?.message}
            disabled={isRequesting}
          />
          <Input
            name="uf"
            variant="standard"
            control={control}
            label="UF"
            lengthInput={2}
            className="number"
            errorText={errors.uf?.message}
            disabled={isRequesting}
          />
        </div>
        <div className="buttonModal">
          <Button
            text="Criar filial"
            dimensions={{ width: '13rem', height: '2.25rem' }}
            onClick={handleSubmit(handleCreateBranch)}
            disabled={isRequesting}
            loading={isRequesting}
          />
        </div>
      </Form>
    </Modal>
  );
}
